import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { Translate } from 'react-localize-redux';
import Modal from '../../../components/general/modal';
import CategoryFeedback, { CategoryFeedbackValue } from './CategoryFeedback';
import { MIN_REASON_LENGTH } from './CorrectIdentifiableModal';
import { QcCategorySection } from './qcCategories';
import { QcRequestState } from './qcRequestState';
import { ReviewerFlagStep, includesOtherReason } from './correctionFlag';
import { AUDIT_TINT } from './qcTokens';
import type { CorrectionFlagStepBody } from '../../../services/qualityCheck';

interface CorrectionFlagModalProps {
    open: boolean;
    /** Which step: flag the visit (reasons, a note with "Other"), resolve or reopen the flag (note only). */
    step: ReviewerFlagStep;
    /** The study's VISIT reasons, as the QC visit read serves them. Only flagging reads them. */
    reasonSections: QcCategorySection[];
    /** Where the step's request has got to; the flag itself is the server's. */
    request: QcRequestState;
    onConfirm: (body: CorrectionFlagStepBody) => void;
    onCancel: () => void;
}

/** No reasons to pick: resolve and reopen keep the reasons the flag was raised with. */
const NO_SECTIONS: QcCategorySection[] = [];

/**
 * SKIN-577 — the reviewer's three steps on a visit's correction flag, in one dialog.
 *
 * One dialog rather than three: each step is "say why, on the record". Flagging says it with
 * reasons, and with a note only when "Other" is among them — the one reason that says nothing
 * by itself; resolving and reopening say it in a note. The reasons and the note are
 * `CategoryFeedback` — the same "pick categories, write a note" the image and lesion feedback
 * use — given the study's VISIT section to flag, and no sections to resolve or reopen, which
 * leaves the note alone.
 *
 * Presentational and controlled like `CorrectIdentifiableModal`, whose shape it follows: it
 * holds what is being typed and nothing else, so every state exists in the catalogue.
 */
export default function CorrectionFlagModal({
    open,
    step,
    reasonSections,
    request,
    onConfirm,
    onCancel,
}: CorrectionFlagModalProps) {
    const [value, setValue] = useState<CategoryFeedbackValue>({ categoryIds: [], comment: '' });

    const flagging = step === 'OPEN';
    const hasReasons = reasonSections.some((section) => section.fields.length > 0);
    const submitting = request.status === 'submitting';
    /**
     * Flagging asks for a note only with "Other"; unpicking it hides the note, and what was
     * typed is not sent — it would explain a reason the flag no longer gives.
     */
    const needsNote = !flagging || includesOtherReason(reasonSections, value.categoryIds);
    const note = needsNote ? value.comment.trim() : '';
    /**
     * The same six-character floor as the identifiable correction, for the same reason: this
     * note is read by the dermatologist who has to act on it and stays on a permanent record,
     * and a note checked only for emptiness collects "ok". The API requires the note too.
     */
    const canConfirm = (!needsNote || note.length >= MIN_REASON_LENGTH)
        && (!flagging || value.categoryIds.length > 0)
        && !submitting;

    const confirm = () => onConfirm(flagging
        ? { reasonFieldIds: value.categoryIds, ...(needsNote ? { note } : {}) }
        : { note });

    return (
        <Modal
            open={open}
            title={<FlagTitle step={step} />}
            // Not closable mid-request: a step that lands after the dialog closed would
            // fold a flag nobody is looking at, or leave its failure for the next dialog.
            closeModal={submitting ? () => undefined : onCancel}
            maxWidth="sm"
            fullWidth
        >
            <Box data-testid="correction-flag-modal" data-step={step} sx={{ width: '100%' }}>
                <Typography variant="body2" color="text.secondary">
                    <Translate id={`hospital.quality-check.correction.modal.body.${step}`} />
                </Typography>

                {flagging && !hasReasons && (
                    <Alert severity="warning" data-testid="flag-no-reasons" sx={{ mt: 2 }}>
                        <Translate id="hospital.quality-check.correction.modal.no-reasons" />
                    </Alert>
                )}

                <Box sx={{ mt: 1.5 }}>
                    <CategoryFeedback
                        categories={{ status: 'ready', sections: flagging ? reasonSections : NO_SECTIONS }}
                        value={value}
                        onChange={submitting ? () => undefined : setValue}
                        notePlaceholderId={`hospital.quality-check.correction.modal.note-placeholder.${step}`}
                        showNote={needsNote}
                    />
                    {/*
                      * Zero-knowledge: everything else about this patient reaches the server
                      * encrypted, and this note does not. The reviewer is the only safeguard.
                      */}
                    {needsNote && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.75 }}
                            data-testid="flag-note-warning"
                        >
                            <Translate id="hospital.quality-check.correction.modal.note-warning" />
                        </Typography>
                    )}
                </Box>

                {request.status === 'failed' && (
                    // What was typed survives: the reviewer retries without rewriting the note.
                    <Alert severity="error" data-testid="flag-failed" sx={{ mt: 2 }}>
                        <Translate id="hospital.quality-check.correction.modal.failed" />
                        {request.error && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {request.error}
                            </Typography>
                        )}
                    </Alert>
                )}

                {/* The action row of CorrectIdentifiableModal, for the same reasons. */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25, mt: 2.5 }}>
                    <Button
                        variant="outlined"
                        color="inherit"
                        disabled={submitting}
                        onClick={onCancel}
                        data-testid="flag-cancel"
                        sx={{ fontWeight: 600 }}
                    >
                        <Translate id="general.cancel" />
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!canConfirm}
                        onClick={confirm}
                        data-testid="flag-confirm"
                        startIcon={<CheckIcon />}
                        sx={{ fontWeight: 600 }}
                    >
                        <Translate
                            id={submitting
                                ? 'hospital.quality-check.correction.modal.saving'
                                : `hospital.quality-check.correction.modal.confirm.${step}`}
                        />
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}

/** The audit tag, then the step's title — the identifiable correction's header. */
function FlagTitle({ step }: { step: ReviewerFlagStep }) {
    return (
        <Box>
            <Chip
                size="small"
                icon={<FlagOutlinedIcon fontSize="small" />}
                label={<Translate id="hospital.quality-check.correction.modal.tag" />}
                sx={{ bgcolor: AUDIT_TINT.bg, color: AUDIT_TINT.fg, fontWeight: 600 }}
            />
            <Typography variant="h6" component="h3" sx={{ mt: 1.5, fontWeight: 600 }} data-testid="flag-title">
                <Translate id={`hospital.quality-check.correction.modal.title.${step}`} />
            </Typography>
        </Box>
    );
}
